import jsonLogic from 'json-logic-js';

import { generateComponentLogicJsonSchema } from 'dashboard/apps/InquiryTemplateEditor/hooks/useComponentLogicJsonSchema';
import { WorkflowVersionMap } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { COMPONENT_JSONLOGIC_KEYS } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/component-constants';
import {
  ComponentValidationError,
  ValidationError,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/component-validations/types';
import {
  ComponentJsonLogicKey,
  ComponentJsonLogicRule,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/types';
import {
  formatJSONLogicRule,
  isClientLogicKey,
  isConfigJSONLogic,
  isSupportedClientJsonLogicOperator,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/utils';
import { getDisplayNameForStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import { getSchemaForFieldPath } from 'dashboard/components/JsonSchemaSelect';
import {
  ConditionalRule,
  ConditionalRuleValueConfig,
  getConditionalRuleParts,
  LogicBlockV2,
  RuleValueTypes,
} from 'dashboard/lib/workflows';
import {
  InquiryTemplateVersionStep,
  InquiryTemplateVersionStepUi,
} from 'dashboard/models/inquiry-template-version';
import { WorkflowOperatorKey } from 'dashboard/models/workflow';
import { FieldSchema } from 'lib/fields';
import { isUiFormComponentConfig, UiComponentConfig } from 'lib/interfaces/ui-components';
import { JsonSchema } from 'lib/json-schema';
import { hasNoPossibilities, isMultiValue } from 'lib/workflows';

// currently mainly intended for internal debugging -
// will update for external display as rollout progresses
export const getErrorMessagePrefix = (
  step: InquiryTemplateVersionStepUi,
  componentConfig: UiComponentConfig,
): string => {
  return `${componentConfig.name} (${getDisplayNameForStep(step)}):`;
};

const isComponentConfigLogicKey = (
  key: string,
  componentConfig: UiComponentConfig['config'],
): key is keyof UiComponentConfig['config'] => {
  if (componentConfig == null) return false;
  return key in componentConfig;
};

const validateFieldPaths = ({
  ruleValueConfig,
  jsonSchema,
  logicKey,
}: {
  ruleValueConfig: ConditionalRuleValueConfig | undefined;
  jsonSchema: JsonSchema;
  logicKey: string;
}): string[] => {
  if (ruleValueConfig == null || ruleValueConfig?.type !== RuleValueTypes.Field) return [];
  return ruleValueConfig.paths
    .filter((path) => getSchemaForFieldPath({ path, initialSchema: jsonSchema }).schema == null)
    .map((path) => `"${logicKey}" has invalid path ${path}`);
};

const validateOperator = (
  operator: WorkflowOperatorKey | undefined,
  logicKey: ComponentJsonLogicKey,
): string | undefined => {
  const isClientRule = isClientLogicKey(logicKey);
  if (
    operator == null ||
    (isClientRule && !isSupportedClientJsonLogicOperator(operator)) ||
    (!isClientRule && !Object.values(WorkflowOperatorKey).includes(operator))
  ) {
    return `"${logicKey}" has invalid operator: ${operator ?? 'nil'}`;
  }
};

const validateValues = ({
  rule,
  logicKey,
}: {
  rule: ConditionalRule;
  logicKey: ComponentJsonLogicKey;
}): string | undefined => {
  const { operator, valueRightConfig } = getConditionalRuleParts(rule);
  const rightValue = valueRightConfig?.value;

  // comparison operators such as '==' should have 2 rule parts - the compared field and expected value.
  // unary operators such as 'isBlank' only have 1 rule part for the compared field.
  const expectedNumRuleParts = hasNoPossibilities(operator) ? 1 : 2;
  const numRulePartsMismatch = rule[operator]?.length !== expectedNumRuleParts;

  if (numRulePartsMismatch) {
    const numParts = rule[operator]?.length ?? 0;
    return `"${logicKey}" has incorrect number (${numParts}) of rule parts for unary operator ${operator}`;
  }

  // operators comparing against multiple values such as includesall should have a non-empty array of values
  const isMissingComparedValues =
    isMultiValue(operator) && Array.isArray(rightValue) && rightValue.length === 0;

  if (isMissingComparedValues) {
    return `"${logicKey}" is missing compared values for operator: ${operator}`;
  }
};

const validateConditionalRule = ({
  rule,
  jsonSchema,
  logicKey,
}: {
  rule: ConditionalRule;
  jsonSchema: JsonSchema;
  logicKey: ComponentJsonLogicKey;
}): string[] => {
  const { operator, valueLeftConfig, valueRightConfig } = getConditionalRuleParts(rule);

  const errorMessages: Array<string | undefined> = [validateOperator(operator, logicKey)];

  if (valueLeftConfig?.type !== RuleValueTypes.Field) {
    errorMessages.push(`"${logicKey}" must have a field as the left value`);
  }

  errorMessages.push(validateValues({ rule, logicKey }));

  return [
    ...errorMessages,
    ...validateFieldPaths({
      ruleValueConfig: valueLeftConfig,
      jsonSchema,
      logicKey,
    }),
    ...validateFieldPaths({
      ruleValueConfig: valueRightConfig,
      jsonSchema,
      logicKey,
    }),
  ].filter((message): message is string => message != null);
};

const isEmptyBlock = (block: LogicBlockV2[] | ConditionalRule[]): boolean => {
  if (block.length === 0) return true;
  else if (block.length === 1) {
    // default new rules are initialized as: [{}]
    return typeof block[0] === 'object' && Object.keys(block[0]).length === 0;
  }
  return false;
};

const validateLogicRulesRecursive = ({
  rule,
  jsonSchema,
  logicKey,
}: {
  rule: ComponentJsonLogicRule;
  jsonSchema: JsonSchema;
  logicKey: ComponentJsonLogicKey;
}): string[] => {
  const operator = jsonLogic.get_operator(rule);
  const values = jsonLogic.get_values(rule);
  switch (true) {
    case operator === 'and':
    case operator === 'or':
      return isEmptyBlock(values)
        ? [`"${logicKey}" has empty "${operator} rule" block`]
        : values.flatMap((value: ConditionalRule) =>
            validateLogicRulesRecursive({ rule: value, jsonSchema, logicKey }),
          );
    default:
      return validateConditionalRule({ rule: rule as ConditionalRule, jsonSchema, logicKey });
  }
};

const validateComponentConfigLogicRules = ({
  componentConfig,
  currentStep,
  steps,
  fieldSchema,
  workflowVersionMap,
}: {
  componentConfig: UiComponentConfig;
  currentStep: InquiryTemplateVersionStepUi;
  steps: InquiryTemplateVersionStep[];
  fieldSchema: FieldSchema[];
  workflowVersionMap: WorkflowVersionMap;
}): ComponentValidationError[] => {
  if (componentConfig.config == null) return [];
  const logicRuleKeys = COMPONENT_JSONLOGIC_KEYS.filter((logicKey) => {
    return (
      isComponentConfigLogicKey(logicKey, componentConfig.config) &&
      isConfigJSONLogic(componentConfig.config[logicKey])
    );
  });

  const optionalRule = isUiFormComponentConfig(componentConfig)
    ? componentConfig.config.optional?.logic
    : undefined;

  if (logicRuleKeys.length === 0 && (optionalRule == null || typeof optionalRule === 'boolean'))
    return [];

  const jsonSchema = generateComponentLogicJsonSchema({
    currentStep,
    currentComponentConfig: componentConfig,
    steps,
    fieldSchema,
    workflowVersionMap,
  });

  let logicValidationErrors = logicRuleKeys.flatMap((logicKey) =>
    validateLogicRulesRecursive({
      rule: formatJSONLogicRule(
        componentConfig.config[logicKey as keyof (typeof componentConfig)['config']],
      ).rules,
      jsonSchema: isClientLogicKey(logicKey) ? jsonSchema.client : jsonSchema.server,
      logicKey,
    }),
  );

  if (optionalRule != null && typeof optionalRule !== 'boolean') {
    logicValidationErrors = logicValidationErrors.concat(
      validateLogicRulesRecursive({
        rule: formatJSONLogicRule(optionalRule as ComponentJsonLogicRule).rules,
        jsonSchema: jsonSchema.client,
        logicKey: 'optional',
      }),
    );
  }

  return logicValidationErrors.map((errorMessage) => ({
    type: ValidationError.LogicRuleInvalid,
    componentType: componentConfig.type,
    stepName: currentStep.name,
    message: `${getErrorMessagePrefix(currentStep, componentConfig)} ${errorMessage}`,
  }));
};

export default validateComponentConfigLogicRules;
